import type { MomentInput } from 'moment';
import { getMinutesFromDateTime, setTimeFromMinutes } from 'framework/utils';
import { FocusTimeSlot } from '../types';
import {
  STEP_MINUTES,
  TIMELINE_START_MINUTES,
  TIMELINE_END_MINUTES,
  timelineDuration,
  DEFAULT_SLOT_MINUTES,
  MIN_SLOT_MINUTES,
  FOCUS_SLOT_PAST_GRACE_MINUTES,
} from '../constants';

const LOCAL_FOCUS_SLOT_ID_PREFIX = 'local-focus-';

export const clampToRange = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

export const roundToStep = (minutes: number) => {
  return Math.round(minutes / STEP_MINUTES) * STEP_MINUTES;
};

const ceilToStep = (minutes: number) => {
  return Math.ceil(minutes / STEP_MINUTES) * STEP_MINUTES;
};

export const getMinSelectableMinutes = (date?: MomentInput) => {
  return clampToRange(ceilToStep(getMinutesFromDateTime(date)), TIMELINE_START_MINUTES, TIMELINE_END_MINUTES);
};

export const getMinCreatableMinutes = (date?: MomentInput) => {
  return clampToRange(
    ceilToStep(getMinutesFromDateTime(date) - FOCUS_SLOT_PAST_GRACE_MINUTES),
    TIMELINE_START_MINUTES,
    TIMELINE_END_MINUTES,
  );
};

export const getMinSelectableSlotEndMinutes = (date?: MomentInput) => {
  return clampToRange(ceilToStep(getMinutesFromDateTime(date)), TIMELINE_START_MINUTES, TIMELINE_END_MINUTES);
};

const minutesToPercent = (minutes: number) => {
  return ((minutes - TIMELINE_START_MINUTES) / timelineDuration) * 100;
};

const clampToTimeline = (minutes: number) => {
  return clampToRange(minutes, TIMELINE_START_MINUTES, TIMELINE_END_MINUTES);
};

export const minutesToTimelinePercent = (minutes: number) => {
  return minutesToPercent(clampToTimeline(minutes));
};

export const clientXToTimelineMinutes = (clientX: number, element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  const ratio = clampToRange((clientX - rect.left) / rect.width, 0, 1);
  return roundToStep(TIMELINE_START_MINUTES + ratio * timelineDuration);
};

export const clientYToTimelineMinutes = (clientY: number, element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  const ratio = clampToRange((clientY - rect.top) / rect.height, 0, 1);

  return roundToStep(TIMELINE_START_MINUTES + ratio * timelineDuration);
};

export const getMinMovableStartMinutes = (currentMinutes: number | null) => {
  if (currentMinutes === null) {
    return TIMELINE_START_MINUTES;
  }

  return Math.max(ceilToStep(currentMinutes - FOCUS_SLOT_PAST_GRACE_MINUTES), TIMELINE_START_MINUTES);
};

export const isTimelineMinutesVisible = (minutes: number | null): minutes is number => {
  return minutes !== null && minutes >= TIMELINE_START_MINUTES && minutes <= TIMELINE_END_MINUTES;
};

export const getTimelineHourMarks = () => {
  const startHour = TIMELINE_START_MINUTES / 60;
  const endHour = TIMELINE_END_MINUTES / 60;

  return Array.from({ length: endHour - startHour + 1 }, (_, index) => startHour + index);
};

export const getDurationLabel = (startMinutes: number, endMinutes: number) => {
  const duration = endMinutes - startMinutes;
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;

  if (hours && minutes) {
    return `${hours}h ${minutes}m`;
  }

  return hours ? `${hours}h` : `${minutes}m`;
};

export const getTimeLabel = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;

  return `${hours}:${String(restMinutes).padStart(2, '0')}`;
};

export const getSlotStartMinutes = (slot: FocusTimeSlot) => {
  return getMinutesFromDateTime(slot.startAt);
};

export const getSlotEndMinutes = (slot: FocusTimeSlot) => {
  return getMinutesFromDateTime(slot.endAt);
};

export const createFocusSlot = (
  id: string,
  startMinutes: number,
  endMinutes: number,
  date?: MomentInput,
): FocusTimeSlot => {
  return {
    id,
    startAt: setTimeFromMinutes(startMinutes, date),
    endAt: setTimeFromMinutes(endMinutes, date),
  };
};

export const createSlotId = () => {
  return `${LOCAL_FOCUS_SLOT_ID_PREFIX}${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const isLocalFocusSlotId = (id: string) => {
  return id.startsWith(LOCAL_FOCUS_SLOT_ID_PREFIX);
};

export const createDefaultFocusSlot = (date?: MomentInput): FocusTimeSlot => {
  const startMinutes = getMinCreatableMinutes(date);
  const endMinutes = clampToRange(startMinutes + DEFAULT_SLOT_MINUTES, startMinutes, TIMELINE_END_MINUTES);

  return createFocusSlot(createSlotId(), startMinutes, endMinutes, date);
};

export const isValidSlot = (slot: FocusTimeSlot) => {
  const startMinutes = getSlotStartMinutes(slot);
  const endMinutes = getSlotEndMinutes(slot);

  return (
    startMinutes >= TIMELINE_START_MINUTES &&
    endMinutes <= TIMELINE_END_MINUTES &&
    endMinutes - startMinutes >= MIN_SLOT_MINUTES
  );
};

export const overlapsAny = (slot: FocusTimeSlot, slots: FocusTimeSlot[]) => {
  return slots.some(candidate => {
    if (candidate.id === slot.id) {
      return false;
    }

    const startMinutes = getSlotStartMinutes(slot);
    const endMinutes = getSlotEndMinutes(slot);
    const candidateStartMinutes = getSlotStartMinutes(candidate);
    const candidateEndMinutes = getSlotEndMinutes(candidate);

    return !(endMinutes + STEP_MINUTES <= candidateStartMinutes || startMinutes >= candidateEndMinutes + STEP_MINUTES);
  });
};
